import {Injectable, NgZone} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {BehaviorSubject, firstValueFrom, Observable} from 'rxjs';
import {ApiEndPoint} from '../../utility/api-end-point';
import {AppEnumConstants} from '../../enums/app-enum-constants';
import {NotificationService} from '../notification/notification.service';
import {CommonUtility} from '../../utility/common-utility';
import {HttpResponseMessage} from '../../utility/http-response-message';

@Injectable({
  providedIn: 'root'
})
export class AuthOktaService {

  public userDetail;
  private organizationId: any | null = null;
  private isLoadingOrgId = false;
  private commonUtility = new CommonUtility();
  public logoutSubject = new BehaviorSubject(null);

  constructor(public auth: AuthService, private httpClient: HttpClient, private zone: NgZone,
              private notificationService: NotificationService) {
  }

  public async signIn(orgId?): Promise<void> {
    orgId = this.organizationId;
    const options: any = {
      authorizationParams: {}
    };

    // Add org if provided
    if (orgId) {
      options.authorizationParams.organization = orgId;
    }
    await this.auth.loginWithRedirect(options);
  }

  public async signOut(data?: any): Promise<void> {
    const options = {
      logoutParams: {
        returnTo: window.location.origin
      }, ...data
    };
    await this.auth.logout(options);
  }

  /**
   * Synchronously check if organization ID is available (for MFA check)
   * Returns the cached organization ID or null
   */
  public hasOrganizationId(): string | null {
    // Return the cached value directly (no API call)
    return this.organizationId || null;
  }

  async getOrganizationId(): Promise<string | null> {
    const domainName = environment.domainName.replace('.papertrl.com', '');

    // Check memory cache first (for current session)
    if (this.organizationId) {
      // Valid org ID - Auth0/MFA mode
      return this.organizationId;
    } else if (this.organizationId === false) {
      // Legacy mode for this session
      return null;
    }

    // If already loading, wait a bit and return null to prevent duplicate calls
    if (this.isLoadingOrgId) {
      return null;
    }

    // Check localStorage
    const cachedOrgId = localStorage.getItem(AppEnumConstants.ORGANIZATION_ID);
    if (cachedOrgId !== null && cachedOrgId !== undefined) {
      // localStorage key exists
      if (cachedOrgId === '') {
        // Empty string - call API once per session to check if org is now configured
        this.isLoadingOrgId = true;
        try {
          return await this.getOrganization(domainName);
        } catch (error) {
          // API error - use legacy mode for this session and cache the error
          this.organizationId = false;
          localStorage.setItem(AppEnumConstants.ORGANIZATION_ID, '');
          this.commonUtility.logErrorToSentry(error, {
            tags: {
              errorType: AppEnumConstants.AUTH0,
              errorGroup: AppEnumConstants.AUTH
            },
            extra: {
              domainName,
              timestamp: new Date().toISOString(),
              component: AppEnumConstants.AUTH0,
              method: 'getOrganizationId',
              error
            }
          });
          return null;
        } finally {
          this.isLoadingOrgId = false;
        }
      } else {
        // Valid organization ID cached - use Auth0/MFA mode
        this.organizationId = cachedOrgId;
        return cachedOrgId;
      }
    }

    // No localStorage - call API once per session to check
    try {
      return await this.getOrganization(domainName);

    } catch (error) {
      // API error - use legacy mode and cache to prevent retry loop
      this.organizationId = false;
      localStorage.setItem(AppEnumConstants.ORGANIZATION_ID, '');
      this.commonUtility.logErrorToSentry(error, {
        tags: {
          errorType: AppEnumConstants.AUTH0,
          errorGroup: AppEnumConstants.AUTH
        },
        extra: {
          domainName,
          timestamp: new Date().toISOString(),
          component: AppEnumConstants.AUTH0,
          method: 'getOrganizationId',
          error
        }
      });
      return null;
    }
  }

  async getOrganization(domainName) {
    const response = await firstValueFrom(
      this.httpClient.get(
        `${ApiEndPoint.API_URL}/tenant_management/get_organization_id`,
        {
          params: {domainName},
          responseType: 'text'
        }
      )
    );

    // Backend returns plain text: either "false" or the organization ID string
    if (response === 'false' || !response || response.trim() === '') {
      // Backend says no organization - use legacy mode for this session
      this.organizationId = false;
      // Don't set localStorage - will check API again on next page refresh
      return null;
    }

    // Valid organization ID - store and use Auth0/MFA mode
    this.organizationId = response.trim();
    this.logoutSubject.next(true);
    localStorage.setItem(AppEnumConstants.ORGANIZATION_ID, response.trim());
    setTimeout(() => {
      window.location.reload();
    }, 1000);
    return response.trim();
  }


  updateOrg(metaData) {
    const orgId = metaData.orgId;
    const metadata = metaData.metadata;
    return this.httpClient.post(ApiEndPoint.API_URL + '/api/update-org', {orgId, metadata});
  }

  addUsersOrg(metaData) {
    const orgId = metaData.orgId;
    const userIds = metaData.userIds;
    return this.httpClient.post(ApiEndPoint.API_URL + '/api/add-users-to-org', {orgId, userIds});
  }

  public getUsers() {
    return this.httpClient.get('http://localhost:8082/api/get-users', {observe: 'response'});
  }

  enableMfa(mfaData: any) {
    return this.httpClient.post(ApiEndPoint.API_URL + '/auth/mfa-ticket', {mfaData});
  }

  markMfaEnabled() {
    const userId = this.userDetail.sub;
    const metadata = {mfa_enabled: true};

    this.httpClient.post(ApiEndPoint.API_URL + '/api/update-user', {userId, metadata})
      .subscribe(() => {
        console.log('MFA enabled flag updated');
      });
  }

  getEnrollments() {
    const userId = this.userDetail.sub;
    const metadata = {mfa_enabled: true};

    this.httpClient.get(ApiEndPoint.API_URL + '/api/mfa/enrollments', {params: {userId}})
      .subscribe(() => {
        console.log('MFA enabled flag updated');
      });
  }

  getUserDetails() {
    return new Promise(resolve => this.auth.user$.subscribe(value => {
      this.userDetail = value;
      resolve(value);
    }));
  }

  listenForMfa(userId: string): Observable<any> {
    return new Observable(observer => {
      const eventSource = new EventSource(ApiEndPoint.API_URL + `/events?userId=${userId}`);
      eventSource.addEventListener('mfaEnabled', (event: any) => {
        this.zone.run(() => {
          observer.next(JSON.parse(event.data));
        });
      });

      eventSource.onerror = (error) => {
        console.error('SSE error', error);
        eventSource.close();
        observer.error(error);
      };

      return () => eventSource.close();
    });
  }

  /**
   * Send password reset email using Auth0's public database connection endpoint
   */
  resetPassword(username: any) {
    this.httpClient.post(ApiEndPoint.API_URL + `/tenant_management/reset-auth0-password`, null
      , {observe: 'response', params: {username}}).subscribe({
      next: (response) => {
        this.notificationService.successMessage(HttpResponseMessage.PASSWORD_RESET_LINK_SEND_SUCCESSFULLY);
      },
      error: (error) => {
        console.error('Password reset error', error);
      }
    });
  }

  /**
   * Send email verification using Auth0's public database connection endpoint
   * @param userEmail Optional email address to resend verification to
   */
  resendEmailVerification(userEmail?: string) {
    return this.httpClient.get(ApiEndPoint.API_URL + `/tenant_management/email-verification-resend`,
      {observe: 'response', params: {userEmail}});
  }
}
